// Failure-only, allowlisted metadata. Never inspect request/response bodies,
// headers, email addresses, credentials, full URLs, or raw error messages.
export function authEndpoint(raw: string): string | undefined {
  try {
    const url = new URL(raw, window.location.origin)
    if (url.origin === 'https://identitytoolkit.googleapis.com') {
      if (url.pathname === '/') return 'firebase'
      return ({ '/v1/accounts:signInWithPassword': 'password', '/v1/accounts:lookup': 'account',
        '/v1/accounts:signUp': 'registration', '/v2/recaptchaConfig': 'recaptcha' } as Record<string, string>)[url.pathname]
    }
    if (['https://www.google.com', 'https://www.gstatic.com', 'https://www.recaptcha.net'].includes(url.origin) && url.pathname.startsWith('/recaptcha/')) return 'recaptcha'
    if (url.origin === 'https://securetoken.googleapis.com' && url.pathname === '/v1/token') return 'token'
    if (url.origin === window.location.origin) {
      return ({ '/api/auth/csrf': 'csrf', '/api/auth/session': 'session', '/api/me': 'profile',
        '/api/auth/verification-email': 'verification' } as Record<string, string>)[url.pathname]
    }
  } catch { /* Not a recognized endpoint. */ }
}

const knownCodes = new Set(['auth/network-request-failed', 'auth/invalid-credential', 'auth/too-many-requests',
  'auth/user-disabled', 'auth/invalid-email', 'auth/email-already-in-use', 'auth/weak-password',
  'auth/operation-not-allowed', 'auth/internal-error', 'auth/invalid-api-key', 'auth/unauthorized-domain',
  'auth/popup-blocked', 'auth/popup-closed-by-user', 'auth/multi-factor-auth-required', 'auth/invalid-verification-code'])

export function diagnosticCode(error: unknown): string {
  const code = error && typeof error === 'object' && 'code' in error ? error.code : undefined
  return typeof code === 'string' && knownCodes.has(code) ? code : 'other'
}

function browserFamily(): string {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua)) return 'edge'
  if (/Firefox\/|FxiOS\//.test(ua)) return 'firefox'
  if (/Chrome\/|CriOS\//.test(ua)) return 'chrome'
  if (/Safari\//.test(ua)) return 'safari'
  return 'other'
}

type RequestObservation = { endpoint: string; outcome: 'pending' | 'http' | 'network'; status: number; elapsedMs: number }
const elapsed = (start: number) => Math.min(600000, Math.max(0, Math.round(performance.now() - start)))

export async function withAuthDiagnostics<T>(flow: 'login' | 'register' | 'mfa' | 'google', action: () => Promise<T>): Promise<T> {
  const started = performance.now()
  const originalFetch = window.fetch
  const observations: { value: RequestObservation; started: number }[] = []
  const blocked = new Set<string>()
  const onBlocked = (event: SecurityPolicyViolationEvent) => {
    const endpoint = authEndpoint(event.blockedURI)
    if (endpoint) blocked.add(endpoint)
  }
  // Observe only the auth attempt's known endpoints; forward the original
  // request unchanged and restore fetch even when authentication fails.
  const observedFetch: typeof fetch = async (input, init) => {
    const endpoint = authEndpoint(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url)
    const value: RequestObservation | undefined = endpoint && observations.length < 20
      ? { endpoint, outcome: 'pending', status: 0, elapsedMs: 0 } : undefined
    const requestStart = performance.now()
    if (value) observations.push({ value, started: requestStart })
    try {
      const response = await originalFetch.call(window, input, init)
      if (value) { value.outcome = 'http'; value.status = response.status; value.elapsedMs = elapsed(requestStart) }
      return response
    } catch (error) {
      if (value) { value.outcome = 'network'; value.elapsedMs = elapsed(requestStart) }
      throw error
    }
  }
  window.fetch = observedFetch
  document.addEventListener('securitypolicyviolation', onBlocked)
  try {
    return await action()
  } catch (error) {
    const id = crypto.randomUUID()
    const code = diagnosticCode(error)
    const report = { version: 1, id, flow, code, elapsedMs: elapsed(started), browser: browserFamily(),
      online: navigator.onLine, cookiesEnabled: navigator.cookieEnabled,
      requests: observations.map(({ value, started }) => ({ ...value, elapsedMs: value.outcome === 'pending' ? elapsed(started) : value.elapsedMs })),
      blocked: [...blocked] }
    let sent = false
    try {
      const response = await originalFetch.call(window, '/api/auth/diagnostics', {
        method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(report), signal: AbortSignal.timeout(3000),
      })
      sent = response.ok
    } catch { /* Diagnostics must never prevent retrying login. */ }
    const message = code === 'auth/network-request-failed'
      ? 'The sign-in connection failed. Please try again.'
      : error instanceof Error ? error.message : 'Could not sign in.'
    throw new Error(`${message} Reference: ${id}.${sent ? '' : ' Diagnostic report could not be sent.'}`)
  } finally {
    if (window.fetch === observedFetch) window.fetch = originalFetch
    document.removeEventListener('securitypolicyviolation', onBlocked)
  }
}
